import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/** DOCU-914: the panel either shows one entry or the list of all topics. */
export type KnowledgePanelView = 'entry' | 'overview';

export interface KnowledgePanelState {
  isOpen: boolean;
  view?: KnowledgePanelView;
  contextKey?: string;
  helpId?: string;
  /** True as soon as a previous view is on the stack (DOCU-913). */
  canGoBack?: boolean;
}

@Injectable({ providedIn: 'root' })
export class KnowledgePanelService {
  private state$ = new BehaviorSubject<KnowledgePanelState>({ isOpen: false });

  /**
   * DOCU-913: views visited before the current one, most recent last.
   * Without it, following a related topic replaced the entry with no way back;
   * since DOCU-914 it also carries the way back from an entry to the topic list.
   */
  private history: KnowledgePanelState[] = [];

  get panelState$() {
    return this.state$.asObservable();
  }

  /** Opened from a page's help button - this starts a new trail. */
  open(contextKey: string): void {
    this.history = [];
    this.state$.next({ isOpen: true, view: 'entry', contextKey, helpId: undefined, canGoBack: false });
  }

  openById(helpId: string): void {
    const current = this.state$.value;

    if (current.isOpen && current.view !== 'overview' && current.helpId === helpId) {
      return;
    }

    this.pushCurrent(current);
    this.state$.next({ isOpen: true, view: 'entry', helpId, contextKey: undefined, canGoBack: this.history.length > 0 });
  }

  /** DOCU-914: list of all topics, reachable from the panel header. */
  openOverview(): void {
    const current = this.state$.value;

    if (current.isOpen && current.view === 'overview') {
      return;
    }

    this.pushCurrent(current);
    this.state$.next({ isOpen: true, view: 'overview', contextKey: undefined, helpId: undefined, canGoBack: this.history.length > 0 });
  }

  back(): void {
    const previous = this.history.pop();
    if (!previous) {
      return;
    }
    this.state$.next({ ...previous, canGoBack: this.history.length > 0 });
  }

  close(): void {
    this.history = [];
    this.state$.next({ isOpen: false });
  }

  private pushCurrent(current: KnowledgePanelState): void {
    if (current.isOpen && (current.contextKey || current.helpId || current.view === 'overview')) {
      this.history.push(current);
    }
  }
}
